// apps/web/src/features/finance/revenue/queries.ts (#602)
// React Query key factory + hook for the admin-only revenue dashboard.
//
// Cache policy (deliberate)
// -------------------------
//  - `staleTime: 5*60_000` — matches the backend's 5-minute in-memory
//    cache TTL so navigating away and back during the same window
//    re-uses the same fetched payload without a network round-trip.
//  - `refetchOnWindowFocus: false` — refresh feeds operator-driven
//    decisions; an unsolicited refetch on focus can mutate visible
//    numbers without an explicit user action.
//  - `refetchInterval: false` — no polling; the user reloads manually.
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { RevenueDashboard, RevenueMonthKey } from '@magimp/shared';
import { fetchRevenueDashboard } from './api.js';

export const revenueKeys = {
  all: ['finance-revenue'] as const,
  dashboard: (monthKey: RevenueMonthKey | undefined) =>
    ['finance-revenue', 'dashboard', monthKey ?? '__current__'] as const,
};

const REVENUE_STALE_MS = 5 * 60_000;

export function useRevenueDashboard(
  monthKey: RevenueMonthKey | undefined,
  options: Pick<UseQueryOptions<RevenueDashboard>, 'enabled'> = {},
) {
  return useQuery<RevenueDashboard>({
    queryKey: revenueKeys.dashboard(monthKey),
    queryFn: ({ signal }) => fetchRevenueDashboard(monthKey, { signal }),
    enabled: options.enabled ?? true,
    staleTime: REVENUE_STALE_MS,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
}
